// src/components/signage/EstadoDeCarteleria.jsx
//
// Compact status banner for the Estado tab. Shows only the 3 things
// the admin needs at a glance:
//
//   1. Master switch: ON / OFF (large Ant Switch, click to toggle)
//   2. Today's rotor: number of campaigns scheduled + their names
//   3. One connected device: yes/no (badge from /signage/health)
//
// Everything else (per-device list, last-seen IPs, heartbeat) lives in
// a Popover that opens on hover over the device badge. The original
// SignageHealthCard is no longer mounted in this layout.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge, Button, Popover, Skeleton, Switch, Tooltip } from 'antd';
import { ReloadOutlined, CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import {
    getSystemStatus,
    enableSystem,
    disableSystem,
    getSignageHealth,
} from '../../services/signageService';
import './EstadoDeCarteleria.css';

const POLL_INTERVAL_MS = 30_000;

function formatAgo(seconds) {
    if (seconds == null) return '—';
    if (seconds < 60) return `hace ${seconds}s`;
    if (seconds < 3600) return `hace ${Math.floor(seconds / 60)}m`;
    if (seconds < 86_400) return `hace ${Math.floor(seconds / 3600)}h`;
    return `hace ${Math.floor(seconds / 86_400)}d`;
}

// Argentina is fixed UTC-3, no DST. Format in 24h, in ART, so the
// admin always sees wall-clock time regardless of their browser TZ.
function formatArgTime(date) {
    if (!date) return '';
    return date.toLocaleTimeString('es-AR', {
        timeZone: 'America/Argentina/Buenos_Aires',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

export default function EstadoDeCarteleria({ systemStatus, setSystemStatus, todayCount, onSnackbar }) {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState(false);
    const [now, setNow] = useState(new Date());
    const pollRef = useRef(null);

    const fetchHealth = useCallback(async () => {
        try {
            const data = await getSignageHealth();
            setHealth(data);
        } catch {
            // health endpoint failure is non-fatal for the banner
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHealth();
        function startPolling() {
            if (pollRef.current) return;
            pollRef.current = setInterval(() => {
                fetchHealth();
                setNow(new Date());
            }, POLL_INTERVAL_MS);
        }
        function stopPolling() {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
        }
        function onVisibility() {
            if (document.hidden) stopPolling();
            else { fetchHealth(); startPolling(); }
        }
        startPolling();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            stopPolling();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [fetchHealth]);

    const enabled = !!systemStatus?.enabled;
    const paused = !!systemStatus?.paused;
    const onlineCount = (health?.devices || []).filter(d => d.status === 'ONLINE').length;
    const oneOnline = onlineCount > 0;
    const oneDevice = (health?.devices || []).filter(d => d.status === 'ONLINE' || d.status === 'STALE').length === 1;

    const handleToggle = useCallback(async (next) => {
        setToggling(true);
        try {
            if (next) {
                const r = await enableSystem();
                setSystemStatus(s => ({ ...s, enabled: true, paused: false }));
                onSnackbar?.(r.message || 'Cartelería activada', 'success');
            } else {
                const r = await disableSystem();
                setSystemStatus(s => ({ ...s, enabled: false, paused: true }));
                onSnackbar?.(r.message || 'Cartelería desactivada', 'info');
            }
        } catch (e) {
            onSnackbar?.('Error: ' + (e.response?.data?.error || e.message), 'error');
        } finally {
            setToggling(false);
        }
    }, [setSystemStatus, onSnackbar]);

    const devicePopover = (
        <div className="estado-device-popover">
            <div className="estado-device-popover__row">
                <strong>{health?.organization?.organizationName || '—'}</strong>
            </div>
            <div className="estado-device-popover__row">
                {loading && <Skeleton.Input active size="small" style={{ width: 120 }} />}
                {!loading && (health?.devices || []).length === 0 && (
                    <span style={{ color: '#999' }}>Sin dispositivos registrados</span>
                )}
                {!loading && (health?.devices || []).map(d => (
                    <div key={d.deviceId} className="estado-device-popover__device">
                        {d.status === 'ONLINE' ? (
                            <CheckCircleTwoTone twoToneColor="#52c41a" />
                        ) : (
                            <CloseCircleTwoTone twoToneColor={d.status === 'EXPIRED' ? '#ff4d4f' : '#999'} />
                        )}
                        <strong>{d.label}</strong>
                        <small>…{d.tokenLastFour}</small>
                        <small>{formatAgo(d.lastSeen?.secondsAgo)}</small>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="estado-carteleria">
            <div className="estado-carteleria__header">
                <h3>Estado de Cartelería</h3>
                <span className="estado-carteleria__ts">
                    {loading ? 'Cargando…' : `Actualizado ${formatArgTime(now)}`}
                </span>
                <Tooltip title="Refrescar">
                    <Button
                        size="small"
                        type="text"
                        icon={<ReloadOutlined />}
                        onClick={fetchHealth}
                    />
                </Tooltip>
            </div>

            <div className="estado-carteleria__row">
                <div className="estado-carteleria__cell">
                    <span className="label">Signage habilitado</span>
                    <Badge
                        status={enabled ? 'success' : 'default'}
                        text={enabled ? 'Sí' : 'No'}
                    />
                </div>

                <div className="estado-carteleria__cell">
                    <span className="label">Cartelería</span>
                    <Switch
                        checked={enabled}
                        onChange={handleToggle}
                        loading={toggling}
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                    />
                </div>

                <div className="estado-carteleria__cell">
                    <span className="label">Rotativo de hoy</span>
                    <span>
                        <strong>{todayCount}</strong>{' '}
                        {todayCount === 1 ? 'campaña' : 'campañas'}
                    </span>
                </div>

                <div className="estado-carteleria__cell">
                    <span className="label">Dispositivo</span>
                    <Popover
                        content={devicePopover}
                        title="Detalle de dispositivos"
                        trigger="hover"
                        placement="bottomRight"
                    >
                        {loading ? (
                            <Badge status="processing" text="Verificando…" />
                        ) : oneOnline ? (
                            <Badge status="success" text={oneDevice ? '1 en línea' : `${onlineCount} en línea`} />
                        ) : (
                            <Badge status="warning" text="Sin conexión" />
                        )}
                    </Popover>
                </div>
            </div>
        </div>
    );
}
